export type WeekDayKey = "SUN" | "MON" | "TUE" | "WED" | "THU" | "FRI" | "SAT";
type AmPm = "AM" | "PM";
type WeekDayDetails = {
  label: string;
  cron: 0 | 1 | 2 | 3 | 4 | 5 | 6; // 0 = Sun … 6 = Sat
};
type HourOptions = "1" | "2" | "3" | "4" | "5" | "6" | "7" | "8" | "9" | "10" | "11" | "12";
type MinuteOptions =
  | "00"
  | "05"
  | "10"
  | "15"
  | "20"
  | "25"
  | "30"
  | "35"
  | "40"
  | "45"
  | "50"
  | "55";
export type FormValues = {
  days: WeekDayKey[];
  hour: HourOptions;
  minute: MinuteOptions;
  amPm: AmPm;
};
export type SchedulePickerProps = {
  onSubmit: (cron: string) => Promise<void>;
  layout?: "modal";
  cronExpression?: string;
};

export const DAYS: Record<WeekDayKey, WeekDayDetails> = {
  SUN: { label: "Sun", cron: 0 },
  MON: { label: "Mon", cron: 1 },
  TUE: { label: "Tue", cron: 2 },
  WED: { label: "Wed", cron: 3 },
  THU: { label: "Thu", cron: 4 },
  FRI: { label: "Fri", cron: 5 },
  SAT: { label: "Sat", cron: 6 },
};
const CRON_TO_DAY: WeekDayKey[] = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];

export const HOURS: HourOptions[] = Array.from(
  { length: 12 },
  (_, i) => String(i + 1) as HourOptions
);
export const MINUTES: MinuteOptions[] = [
  "00",
  "05",
  "10",
  "15",
  "20",
  "25",
  "30",
  "35",
  "40",
  "45",
  "50",
  "55",
];

const amPmTo24Hour = (hour: HourOptions, ampm: AmPm): number => {
  if (ampm === "AM" && hour === "12") {
    return 0;
  }
  if (ampm === "AM" || (ampm === "PM" && hour === "12")) {
    return parseInt(hour, 10);
  }

  return parseInt(hour, 10) + 12;
};

export const toCron = ({ days, hour, minute, amPm: ampm }: FormValues): string => {
  let cronDays: string;
  if (days.length === 7) {
    cronDays = "*";
  } else {
    cronDays = days
      .map((key) => DAYS[key].cron)
      .sort((a, b) => a - b)
      .join(",");
  }

  return `${parseInt(minute, 10)} ${amPmTo24Hour(hour, ampm)} * * ${cronDays}`;
};

/**
 * Parses a cron expression and returns the equivalent initial values for the form.
 */
export const getInitialValuesFromCron = (cron?: string): FormValues => {
  if (!cron || typeof cron !== "string") {
    return { days: [], hour: "9", minute: "00", amPm: "AM" };
  }

  const parts = cron.split(" ");
  if (parts.length < 5) {
    return { days: [], hour: "9", minute: "00", amPm: "AM" };
  }

  const [minuteStr, hourStr, _dom, _month, weekDaysStr] = parts;

  const rawMinute = parseInt(minuteStr, 10);
  const snappedMinute = Math.round(rawMinute / 5) * 5;
  const finalMinuteNum = snappedMinute === 60 ? 0 : snappedMinute;
  const minute = finalMinuteNum.toString().padStart(2, "0") as MinuteOptions;

  const rawHour = parseInt(hourStr, 10);
  const amPm: AmPm = rawHour < 12 ? "AM" : "PM";

  let hourNum = rawHour % 12;
  if (hourNum === 0) {
    hourNum = 12;
  }
  const hour = hourNum.toString() as HourOptions;

  let days: WeekDayKey[] = [];
  if (weekDaysStr === "*") {
    days = [...CRON_TO_DAY];
  } else {
    days = weekDaysStr
      .split(",")
      .map((d) => parseInt(d, 10))
      .filter((d) => !isNaN(d) && d >= 0 && d <= 6)
      .map((d) => CRON_TO_DAY[d]);
  }

  return { days, hour, minute, amPm };
};
